import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, Shield, FileText, MessageSquare, Lightbulb } from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminUsersTab } from "@/components/admin/AdminUsersTab";
import { AdminCmsTab } from "@/components/admin/AdminCmsTab";
import { AdminReviewsTab } from "@/components/admin/AdminReviewsTab";
import { AdminProposalsTab } from "@/components/admin/AdminProposalsTab";

const ADMIN_EMAIL = "julie@jvdh.au";

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [activeTab, setActiveTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [foundersList, setFoundersList] = useState([]);
  const [accessGrants, setAccessGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [pendingProposals, setPendingProposals] = useState([]);

  useEffect(() => {
    if (user?.email !== ADMIN_EMAIL) {
      toast.error("Access denied. Admin only.");
      navigate("/dashboard");
      return;
    }
    fetchAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when user changes
  }, [user]);

  useEffect(() => {
    if (activeTab === 'reviews') {
      fetchReviews();
    } else if (activeTab === 'proposals') {
      fetchProposals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on tab change only
  }, [activeTab]);

  const fetchAdminData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
      setUsers(response.data.users);
      setFoundersList(response.data.founders_list || []);
      setAccessGrants(response.data.access_grants || []);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        axios.get(`${API}/reviews/admin/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reviews/public`)
      ]);
      setPendingReviews(pendingRes.data.reviews || []);
      setApprovedReviews(approvedRes.data.reviews || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/voting/proposals/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingProposals(response.data.proposals || []);
    } catch (error) {
      console.error("Error fetching pending proposals:", error);
    }
  };

  const tabs = [
    { id: 'users', label: 'Users & Access', icon: Users, badge: 0, badgeColor: '' },
    { id: 'cms', label: 'Content CMS', icon: FileText, badge: 0, badgeColor: '' },
    { id: 'reviews', label: 'Reviews', icon: MessageSquare, badge: pendingReviews.length, badgeColor: 'bg-amber-500', testid: 'reviews-tab-btn' },
    { id: 'proposals', label: 'Proposals', icon: Lightbulb, badge: pendingProposals.length, badgeColor: 'bg-teal-500', testid: 'proposals-tab-btn' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Shield className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="admin-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Admin Dashboard</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8 flex-wrap">
          {tabs.map(({ id, label, icon: Icon, badge, badgeColor, testid }) => (
            <Button
              key={id}
              variant={activeTab === id ? 'default' : 'outline'}
              onClick={() => setActiveTab(id)}
              className="rounded-xl"
              data-testid={testid}
            >
              <Icon className="w-4 h-4 mr-2" />
              {label}
              {badge > 0 && (
                <span className={`ml-2 px-2 py-0.5 ${badgeColor} text-white text-xs rounded-full`}>
                  {badge}
                </span>
              )}
            </Button>
          ))}
        </div>

        {activeTab === 'users' && (
          <AdminUsersTab
            stats={stats}
            users={users}
            foundersList={foundersList}
            accessGrants={accessGrants}
            token={token}
            onRefresh={fetchAdminData}
          />
        )}

        {activeTab === 'cms' && <AdminCmsTab token={token} />}

        {activeTab === 'reviews' && (
          <AdminReviewsTab
            pendingReviews={pendingReviews}
            approvedReviews={approvedReviews}
            token={token}
            onRefresh={fetchReviews}
          />
        )}

        {activeTab === 'proposals' && (
          <AdminProposalsTab
            pendingProposals={pendingProposals}
            token={token}
            onRefresh={fetchProposals}
          />
        )}
      </main>
    </div>
  );
};

export default AdminPage;
